import { allArticles, allCategories, ARTICLES_PER_PAGE } from '../Data/articles';

export interface ArticleIndexState {
    query: string;
    topic: string;
    demos: boolean;
    page: number;
}

/** URL values are input; unknown topics and malformed page numbers use defaults. */
export function readArticleIndex(params: URLSearchParams): ArticleIndexState {
    const topic = params.get('topic') || 'All';
    const rawPage = params.get('page') || '1';
    const maxPage = Math.max(1, Math.ceil(allArticles.length / ARTICLES_PER_PAGE));
    return {
        query: params.get('q') || '',
        topic: allCategories.includes(topic) ? topic : 'All',
        demos: params.get('demo') === '1',
        page: /^\d+$/.test(rawPage) ? Math.max(1, Math.min(Number(rawPage), maxPage)) : 1,
    };
}

/** Omit defaults so unfiltered writing retains its short, familiar URL. */
export function articleIndexParams(state: ArticleIndexState): URLSearchParams {
    const params = new URLSearchParams();
    if (state.query) params.set('q', state.query);
    if (state.topic !== 'All') params.set('topic', state.topic);
    if (state.demos) params.set('demo', '1');
    if (state.page > 1) params.set('page', String(state.page));
    return params;
}

export function articleIndexPath(state: ArticleIndexState): string {
    const query = articleIndexParams(state).toString();
    return `/articles${query ? `?${query}` : ''}`;
}

/** Reader return links accept only this site's Articles index, never another destination. */
export function articleReturnPath(params: URLSearchParams): string {
    const from = params.get('from');
    if (!from || !/^\/articles(?:\?[^#]*)?$/.test(from)) return '/articles';
    return articleIndexPath(readArticleIndex(new URLSearchParams(from.slice('/articles'.length))));
}

/** Native reader hrefs carry context into a new tab, reload and related articles. */
export function withArticleReturn(articlePath: string, returnPath: string): string {
    const destination = new URL(articlePath, 'https://articles.invalid');
    const validReturn = articleReturnPath(new URLSearchParams({ from: returnPath }));
    if (validReturn !== '/articles') destination.searchParams.set('from', validReturn);
    else destination.searchParams.delete('from');
    return `${destination.pathname}${destination.search}${destination.hash}`;
}
